import { first, groupBy, orderBy } from 'lodash'
import { computed, makeObservable } from 'mobx'
import type { Moment } from 'moment'
import moment from 'moment'

import { Collection } from 'modules/core/lib/mobx-rest'
import createCollectionContext from 'modules/core/store/collections/CollectionContext'

import PlanningVersion, { PLANNING_TOOL } from 'modules/time_planning/store/models/PlanningVersion'

class PlanningVersions extends Collection<PlanningVersion> {
  get cacheKey(): string {
    return 'timePlanningPlanningVersions'
  }

  constructor(data = []) {
    super(data)
    makeObservable(this, {
      shiftManagementTool: computed,
      contractHoursTool: computed,
      workScheduleTool: computed,
      groupedByEmployee: computed,
    })
  }

  model() {
    return PlanningVersion
  }

  get indexes() {
    return ['effective_at', 'employee_id', 'work_schedule_schedule_id']
  }

  get shiftManagementTool() {
    return this.models.filter((model: PlanningVersion) => model.isShiftManagementTool)
  }

  get workScheduleTool() {
    return this.models.filter((model: PlanningVersion) => model.isWorkScheduleTool)
  }

  get contractHoursTool() {
    return this.models.filter((model: PlanningVersion) => model.isContractHoursTool)
  }

  get groupedByEmployee() {
    return groupBy(this.models, (pv: PlanningVersion) => pv.attributes.employee_id)
  }

  defaultPlanningVersion(employeeId: number): PlanningVersion {
    return new PlanningVersion({
      id: 0,
      work_schedule_schedule_id: null,
      default: true,
      employee_id: employeeId,
      planning_tool: PLANNING_TOOL.contractHours,
      effective_at: '01/01/1970',
      number_of_rest_days_in_cents: null,
    })
  }

  getPlanningVersion(planningVersionId: number, employeeId: number) {
    if (planningVersionId === 0) {
      return this.defaultPlanningVersion(employeeId)
    }

    const planningVersion = this.get(planningVersionId)

    if (planningVersion) return planningVersion

    return this.currentEmployeePlanningVersionWithinDate(moment(), employeeId)
  }

  employeePlanningVersions(employeeId: number): PlanningVersion[] {
    const planningVersions = this.groupedByEmployee[employeeId] || []

    return [this.defaultPlanningVersion(employeeId)].concat(planningVersions)
  }

  orderByEffectiveAt(employeeId: number): PlanningVersion[] {
    return orderBy(
      this.employeePlanningVersions(employeeId),
      (pv: PlanningVersion) => pv.effectiveAt,
      'asc'
    )
  }

  isAfterThanNextPlanningVersion(models: PlanningVersion[], index: number, date: Moment) {
    return (
      // last one
      models[index + 1]?.effectiveAt.isAfter(date, 'day') || index === models.length - 1
    )
  }

  isBeforeThanDate(model: PlanningVersion, date: Moment) {
    return model.effectiveAt.isSameOrBefore(date, 'day')
  }

  currentPlanningVersionWithinDate(models: PlanningVersion[], date: Moment) {
    const currentPlanningVersion = models.find(
      (model: PlanningVersion, index: number) =>
        this.isBeforeThanDate(model, date) &&
        this.isAfterThanNextPlanningVersion(models, index, date)
    )

    return currentPlanningVersion || first(models)
  }

  currentEmployeePlanningVersionWithinDate(
    date: Moment,
    employeeId: number
  ): PlanningVersion | undefined {
    return this.currentPlanningVersionWithinDate(this.orderByEffectiveAt(employeeId), date)
  }

  hasShiftManagementVersion(date: Moment, employeeId: number): boolean {
    const planningVersion = this.currentEmployeePlanningVersionWithinDate(date, employeeId)

    return !!planningVersion?.isShiftManagementTool
  }

  isCurrent(planningVersion: PlanningVersion, employeeId: number): boolean {
    const today = moment()
    const current = this.currentEmployeePlanningVersionWithinDate(today, employeeId)

    return current?.attributes.id === planningVersion.attributes.id
  }

  getPrevious(
    employeeId: number,
    planningVersion: PlanningVersion
  ): PlanningVersion | null | undefined {
    const sortedColl = this.orderByEffectiveAt(employeeId)

    const currentIndex = sortedColl.findIndex(
      (pv) => pv.attributes.id === planningVersion.attributes.id
    )

    return sortedColl[currentIndex - 1]
  }
}

const singleton: PlanningVersions = new PlanningVersions()

export default singleton

const { provider: PlanningVersionProvider, hook: usePlanningVersions } = createCollectionContext<
  PlanningVersion,
  PlanningVersions
>()

export { PlanningVersionProvider, usePlanningVersions }
